<template>
  <div class="attributes-tab">
    <div class="card">
      <!-- Фильтры -->
      <div class="flex gap-3 mb-4 flex-wrap">
        <div class="flex-1">
          <IconField iconPosition="left">
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
              v-model="filters.search"
              placeholder="Поиск атрибутов..."
              @input="loadAttributes"
              class="w-full"
            />
          </IconField>
        </div>
        <div>
          <Dropdown
            v-model="filters.show_in_filter"
            :options="filterOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Все атрибуты"
            @change="loadAttributes"
            class="w-full"
          />
        </div>
        <div>
          <Button
            icon="pi pi-refresh"
            label="Обновить кэш"
            class="p-button-outlined"
            :loading="isRefreshing"
            @click="refreshCache"
          />
        </div>
      </div>

      <!-- Таблица атрибутов -->
      <DataTable
        :value="attributes"
        :loading="loading"
        paginator
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50]"
        responsiveLayout="scroll"
        stripedRows
      >
        <Column field="id" header="ID" style="width: 80px"></Column>
        <Column field="name" header="Название"></Column>
        <Column field="field_type" header="Тип">
          <template #body="slotProps">
            <Tag :value="slotProps.data.field_type || 'text'" severity="info" />
          </template>
        </Column>
        <Column field="show_in_filter" header="В фильтрах" style="width: 120px">
          <template #body="slotProps">
            <i :class="slotProps.data.show_in_filter ? 'pi pi-check text-green-500' : 'pi pi-times text-red-500'"></i>
          </template>
        </Column>
        <Column field="required" header="Обязательный" style="width: 140px">
          <template #body="slotProps">
            <i :class="slotProps.data.required ? 'pi pi-check text-green-500' : 'pi pi-times text-red-500'"></i>
          </template>
        </Column>
        <Column header="Действия" style="width: 150px">
          <template #body="slotProps">
            <div class="flex gap-2">
              <Button
                icon="pi pi-eye"
                class="p-button-sm p-button-text"
                v-tooltip.top="'Просмотр'"
                @click="viewAttribute(slotProps.data)"
              />
              <Button
                icon="pi pi-link"
                class="p-button-sm p-button-text p-button-success"
                v-tooltip.top="'Привязать к категориям'"
                @click="showLinkDialog(slotProps.data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Диалог просмотра атрибута -->
    <Dialog
      v-model:visible="showViewDialog"
      header="Детали атрибута"
      :modal="true"
      :style="{ width: '600px' }"
    >
      <div v-if="selectedAttribute" class="p-fluid">
        <div class="field">
          <label class="font-bold">ID:</label>
          <span class="ml-2">{{ selectedAttribute.id }}</span>
        </div>
        <div class="field">
          <label class="font-bold">Название:</label>
          <span class="ml-2">{{ selectedAttribute.name }}</span>
        </div>
        <div class="field">
          <label class="font-bold">Описание:</label>
          <p class="mt-2">{{ selectedAttribute.description || '—' }}</p>
        </div>
        <div class="field">
          <label class="font-bold">Тип поля:</label>
          <Tag :value="selectedAttribute.field_type || 'text'" severity="info" class="ml-2" />
        </div>

        <!-- Значения для списочных атрибутов -->
        <div v-if="selectedAttribute.values && selectedAttribute.values.length > 0" class="field">
          <label class="font-bold">Возможные значения:</label>
          <div class="flex flex-wrap gap-2 mt-2">
            <Chip
              v-for="val in selectedAttribute.values"
              :key="val.id || val.name"
              :label="val.name"
            />
          </div>
        </div>

        <!-- Категории где используется -->
        <div v-if="selectedAttribute.categories && selectedAttribute.categories.length > 0" class="field">
          <label class="font-bold">Используется в категориях:</label>
          <ul class="mt-2">
            <li v-for="catId in selectedAttribute.categories" :key="catId">
              Категория ID: {{ catId }}
            </li>
          </ul>
        </div>
      </div>

      <template #footer>
        <Button label="Закрыть" icon="pi pi-times" class="p-button-text" @click="showViewDialog = false" />
      </template>
    </Dialog>

    <!-- Диалог привязки к категориям -->
    <Dialog
      v-model:visible="showLinkCategoriesDialog"
      header="Привязка атрибута к категориям"
      :modal="true"
      :style="{ width: '700px' }"
    >
      <div v-if="attributeToLink" class="p-fluid">
        <h4>Атрибут: {{ attributeToLink.name }}</h4>
        
        <div class="field mt-4">
          <label>Выберите категории *</label>
          <MultiSelect
            v-model="linkForm.category_ids"
            :options="availableCategories"
            optionLabel="name"
            optionValue="category_id"
            placeholder="Выберите категории"
            :filter="true"
            class="w-full"
          />
          <small v-if="linkErrors.category_ids" class="p-error">{{ linkErrors.category_ids }}</small>
        </div>

        <div class="field-checkbox mt-3">
          <Checkbox id="required" v-model="linkForm.required" :binary="true" />
          <label for="required">Обязательный атрибут</label>
        </div>

        <div class="field mt-3">
          <label for="display_order">Порядок отображения</label>
          <InputNumber
            id="display_order"
            v-model="linkForm.display_order"
            :min="0"
            :max="999"
          />
        </div>
      </div>

      <template #footer>
        <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="showLinkCategoriesDialog = false" />
        <Button
          label="Привязать"
          icon="pi pi-link"
          class="p-button-success"
          :loading="isLinking"
          @click="linkToCategories"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import Chip from 'primevue/chip'
import MultiSelect from 'primevue/multiselect'
import Checkbox from 'primevue/checkbox'
import InputNumber from 'primevue/inputnumber'
import apiService from '../../services/apiService'

// Props
const props = defineProps({
  storeId: {
    type: Number,
    required: true
  }
})

const toast = useToast()

// State
const loading = ref(false)
const isRefreshing = ref(false)
const isLinking = ref(false)

const attributes = ref([])
const availableCategories = ref([])

const filters = ref({
  search: '',
  show_in_filter: null
})

const filterOptions = [
  { label: 'Все атрибуты', value: null },
  { label: 'Только в фильтрах', value: true },
  { label: 'Не в фильтрах', value: false }
]

const showViewDialog = ref(false)
const showLinkCategoriesDialog = ref(false)
const selectedAttribute = ref(null)
const attributeToLink = ref(null)

const linkForm = ref({
  category_ids: [],
  required: false,
  display_order: 0
})

const linkErrors = ref({})

// Загрузка атрибутов
const loadAttributes = async () => {
  loading.value = true
  try {
    const params = { store_id: props.storeId }
    if (filters.value.search) params.search = filters.value.search
    if (filters.value.show_in_filter !== null) params.show_in_filter = filters.value.show_in_filter
    
    const response = await apiService.get('/sync/attributes', { params })
    attributes.value = response.data
  } catch (error) {
    console.error('Ошибка загрузки атрибутов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить атрибуты',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

// Загрузка категорий для привязки
const loadCategories = async () => {
  try {
    const response = await apiService.get('/sync/categories', {
      params: { store_id: props.storeId, limit: 1000 }
    })
    availableCategories.value = response.data.map(item => item.category)
  } catch (error) {
    console.error('Ошибка загрузки категорий:', error)
  }
}

// Обновление кэша атрибутов
const refreshCache = async () => {
  isRefreshing.value = true
  try {
    await apiService.post('/sync/attributes/refresh-cache', {}, {
      params: { store_id: props.storeId }
    })
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Кэш атрибутов обновлен',
      life: 3000
    })
    await loadAttributes()
  } catch (error) {
    console.error('Ошибка обновления кэша:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить кэш',
      life: 3000
    })
  } finally {
    isRefreshing.value = false
  }
}

// Просмотр атрибута
const viewAttribute = async (attribute) => {
  try {
    const response = await apiService.get(`/sync/attributes/${attribute.id}`, {
      params: { store_id: props.storeId }
    })
    selectedAttribute.value = response.data
    showViewDialog.value = true
  } catch (error) {
    console.error('Ошибка загрузки деталей атрибута:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить детали атрибута',
      life: 3000
    })
  }
}

// Показать диалог привязки
const showLinkDialog = (attribute) => {
  attributeToLink.value = attribute
  linkForm.value = {
    category_ids: [],
    required: false,
    display_order: 0
  }
  linkErrors.value = {}
  showLinkCategoriesDialog.value = true
}

// Привязать к категориям
const linkToCategories = async () => {
  linkErrors.value = {}
  
  if (!linkForm.value.category_ids || linkForm.value.category_ids.length === 0) {
    linkErrors.value.category_ids = 'Выберите хотя бы одну категорию'
    return
  }
  
  isLinking.value = true
  try {
    await apiService.post(
      `/sync/attributes/${attributeToLink.value.id}/categories`,
      linkForm.value,
      { params: { store_id: props.storeId } }
    )
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: `Атрибут привязан к ${linkForm.value.category_ids.length} категориям`,
      life: 3000
    })
    
    showLinkCategoriesDialog.value = false
  } catch (error) {
    console.error('Ошибка привязки:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось привязать атрибут',
      life: 5000
    })
  } finally {
    isLinking.value = false
  }
}

// Отслеживание изменения storeId
watch(() => props.storeId, () => {
  loadAttributes()
  loadCategories()
})

onMounted(() => {
  loadAttributes()
  loadCategories()
})
</script>

<style scoped>
.attributes-tab {
  min-height: 600px;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
}
</style>
